import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { BellIcon, CheckMarkIcon, PlusIcon, XIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTransferReminder } from '../../../api/trip-bookings/create-transfer-reminder'
import { updateTransferReminder } from '../../../api/trip-bookings/update-transfer-reminder'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TravelReminderTypeEnumMapping } from '../../../lib/types/enums/travel/travel-reminder-type.enum'
import { TransferReminder } from '../../../lib/types/models/transfer/transfer-reminder'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TransferReminderForm from './transfer-reminder-form'

interface TransferRemindersProps {
  transferId: number
}

const TransferReminders = ({ transferId }: TransferRemindersProps) => {
  const tableRef = useRef<DynamicTableRef<TransferReminder>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TransferReminder
  >(['create', 'edit'])

  const { mutateAsync: createReminder } = useMutation({
    mutationFn: createTransferReminder,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Rappel créé avec succès')
    }
  })

  const { mutateAsync: updateReminder } = useMutation({
    mutationFn: updateTransferReminder,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Rappel modifié avec succès')
    }
  })

  const columns: Column<TransferReminder>[] = [
    {
      accessorKey: 'notified',
      header: 'Notifié',
      cell: ({ row: { original: reminder } }) => (
        <Badge
          text={
            reminder.notified ? (
              <CheckMarkIcon color={tailwindColors.green.DEFAULT} width="18" height="18" />
            ) : (
              <XIcon color={tailwindColors.red.DEFAULT} width="18" height="18" />
            )
          }
          variant="grey"
          className="rounded-full aspect-square justify-center h-5 w-5 p-3.5"
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Type de rappel',
      cell: ({ row: { original: reminder } }) => TravelReminderTypeEnumMapping[reminder.type]
    },
    {
      accessorKey: 'deadline',
      header: 'Date limite',
      cell: ({ row: { original: reminder } }) => (
        <Badge
          text={reminder.deadline ? DateFormatter.DDMMYYYY(reminder.deadline) : '-'}
          variant="grey"
        />
      )
    },
    {
      accessorKey: 'daysBeforeDeadline',
      header: '# de jours avant la date limite',
      cell: ({ row: { original: reminder } }) => (
        <Badge text={reminder.daysBeforeDeadline ?? 0} variant="grey" />
      )
    },
    {
      accessorKey: 'comments',
      header: 'Commentaires',
      cell: ({ row: { original: reminder } }) => reminder.comments ?? '-'
    }
  ]

  return (
    <Fragment>
      <NestedDataTable<TransferReminder, null>
        ref={tableRef}
        title={'Rappels'}
        variant="action"
        deleteMessage={{
          name: 'rappel',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce rappel ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces rappels ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les rappels ?'
        }}
        columns={columns}
        onEditAction={(reminder) => toggleModal('edit', reminder)}
        endpoint={API.transfer.getAllReminders.url.replace(':transferId', transferId.toString())}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un nouveau rappel',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        size="medium"
        icon={BellIcon}
        title="Ajouter un nouveau rappel"
        onCancel={() => toggleModal('create')}
        hideActions
      >
        <TransferReminderForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (values) => createReminder({ ...values, transferId: transferId })
          })}
        />
      </Modal>
      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier le rappel'}
          icon={BellIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TransferReminderForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: (values) =>
                updateReminder({ ...values, transferId: transferId, id: selectedData.id })
            })}
          />
        </Modal>
      )}
    </Fragment>
  )
}

export default TransferReminders
